import type { ListingCategory } from '@/types/listing';
import type { Database } from '@/integrations/supabase/types';
import { normalizeFinish } from '@/constants/cardFinishes';
import { normalizeRarity } from '@/constants/cardRarities';
import { detectGameWithConfidence, detectGame, detectGameFromCollectr } from '@/lib/gameDetection';
import { formatUsdCents } from '@/lib/formatMoney';

// Re-export for backward compatibility
export { detectGame };

// Database-compatible condition type (excludes factory_sealed, sealed, opened)
type DbCondition = Database['public']['Enums']['listing_condition'];

// Collectr CSV column structure
export interface CollectrRow {
  portfolioName: string;
  category: string;
  set: string;
  productName: string;
  cardNumber: string;
  rarity: string;
  variance: string;
  grade: string;
  cardCondition: string;
  averageCostPaid: string;
  quantity: string;
  marketPrice: string;
  priceOverride: string;
  watchlist: string;
  dateAdded: string;
  notes: string;
}

// Parsed item ready for insertion
export interface ParsedCollectrItem {
  title: string;
  category: ListingCategory;
  condition: DbCondition;
  quantity: number;
  
  // Optional fields
  set_name?: string;
  card_number?: string;
  rarity?: string;
  finish?: string;
  language?: string;
  game?: string;
  
  // Slab fields
  slab_grade?: number;
  grading_company?: string;
  is_premium_grade?: boolean;
  
  // Edition
  is_first_edition?: boolean;
  
  // Financial
  cost_basis_cents?: number;
  market_price_cents?: number;
  price_override_cents?: number;
  
  // Dates
  purchase_date?: string;
  
  // Notes
  notes?: string;
}

export interface CollectrImportResult {
  success: ParsedCollectrItem[];
  failures: Array<{ line: number; row: string; reason: string }>;
}

/**
 * Detect if a CSV is in Collectr format by checking for expected column headers
 */
export function isCollectrFormat(csvText: string): boolean {
  const firstLine = csvText.split('\n')[0]?.toLowerCase() || '';
  // Collectr CSVs have these distinctive columns
  return (
    firstLine.includes('portfolio name') &&
    firstLine.includes('product name') &&
    firstLine.includes('variance') &&
    firstLine.includes('grade')
  );
}

/**
 * Parse CSV text into rows, handling quoted fields with commas
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  
  // Don't forget the last field
  result.push(current.trim());
  
  return result;
}

/**
 * Detect category based on Grade and Card Number fields
 * - If Grade !== "Ungraded" → slabs
 * - Else if Card Number is empty → sealed
 * - Else → singles
 */
export function detectCategory(grade: string, cardNumber: string): ListingCategory {
  const normalizedGrade = grade.trim().toLowerCase();
  
  if (normalizedGrade !== 'ungraded' && normalizedGrade !== '') {
    return 'slabs';
  }
  
  if (!cardNumber || cardNumber.trim() === '') {
    return 'sealed';
  }
  
  return 'singles';
}

/**
 * Parse grade string like "PSA 10.0" or "CGC 9.0" into company and numeric grade
 */
export function parseGrade(gradeStr: string): { 
  company: string | null; 
  grade: number | null; 
  isPremium: boolean;
} {
  if (!gradeStr || gradeStr.toLowerCase() === 'ungraded') {
    return { company: null, grade: null, isPremium: false };
  }
  
  // Pattern: "PSA 10.0", "CGC 9.0", "BGS 9.5", etc.
  const match = gradeStr.match(/^(PSA|CGC|BGS|SGC|ACE|GMA|HGA)\s*(\d+(?:\.\d+)?)/i);

  if (match) {
    const company = match[1].toUpperCase();
    const grade = parseFloat(match[2]);

    // Premium ONLY when the source string explicitly names a premium designation.
    // CGC 10 = Gem Mint (NOT Pristine). PSA 10 = Gem Mint. BGS 9.5 = Gem Mint.
    const lower = gradeStr.toLowerCase();
    const hasPristine = /\bpristine\b/.test(lower) || /\b10\s*p\b/.test(lower);
    const hasBlackLabel = /\bblack\s*label\b/.test(lower);
    const hasPerfect = /\bperfect\b/.test(lower) || /\bgold\s*label\b/.test(lower);

    let isPremium = false;
    if ((company === 'CGC' || company === 'SGC') && hasPristine && grade === 10) {
      isPremium = true;
    } else if (company === 'BGS' && (hasBlackLabel || hasPristine) && grade === 10) {
      isPremium = true;
    } else if (company === 'PSA' && hasPerfect && grade === 10) {
      isPremium = true;
    }

    return { company, grade, isPremium };
  }

  return { company: null, grade: null, isPremium: false };

}

/**
 * Parse variance string to extract finish and edition info
 * Examples: "Holofoil", "Reverse Holofoil", "1st Edition", "1st Edition Holofoil", 
 *           "Master Ball Reverse Holo", "Unlimited", "Normal"
 */
export function parseVariance(variance: string): { 
  finish: string | null; 
  isFirstEdition: boolean;
} {
  const lower = variance.toLowerCase();
  
  let finish: string | null = null;
  let isFirstEdition = false;
  
  // Check for 1st Edition
  if (lower.includes('1st edition')) {
    isFirstEdition = true;
  }
  
  // Check for finish types (order matters - more specific first)
  if (lower.includes('master ball')) {
    finish = 'master_ball';
  } else if (lower.includes('reverse holo')) {
    finish = 'reverse_holofoil';
  } else if (lower.includes('holofoil') || lower.includes('holo')) {
    finish = 'holofoil';
  } else if (lower === 'normal' || lower === 'unlimited') {
    finish = 'normal';
  }
  
  return { finish, isFirstEdition };
}

/**
 * Map Collectr condition strings to our database condition enum
 */
export function mapCondition(conditionStr: string): DbCondition {
  const lower = conditionStr.toLowerCase().trim();
  
  const conditionMap: Record<string, DbCondition> = {
    'mint': 'mint',
    'near mint': 'near_mint',
    'nm': 'near_mint',
    'lightly played': 'lightly_played',
    'lp': 'lightly_played',
    'moderately played': 'moderately_played',
    'mp': 'moderately_played',
    'heavily played': 'heavily_played',
    'hp': 'heavily_played',
    'damaged': 'damaged',
    'dmg': 'damaged',
  };
  
  return conditionMap[lower] || 'near_mint';
}

/**
 * Detect language from product name or set name.
 * Recognizes explicit tags ("(JP)", "Japanese"), language suffix codes
 * in card numbers (e.g. "067/SV-P", "115/BW-P"), and Japanese-exclusive
 * product lines (Expert Deck, GX Starter Set, JP promo sets, etc.).
 */
export function detectLanguage(text: string): string {
  if (!text) return 'english';
  const lower = text.toLowerCase();

  // ---- Japanese ----
  if (lower.includes('(jp)') || lower.includes('japanese') || lower.includes('(japan)') || /\bjp\b/.test(lower)) {
    return 'japanese';
  }
  // CJK kana/hiragana/katakana ⇒ Japanese
  if (/[\u3040-\u309f\u30a0-\u30ff]/.test(text)) {
    return 'japanese';
  }
  if (/\/(sv|sm|swsh|bw|xy|dp|hgss|pcg|adv)-p\b/i.test(text)) {
    return 'japanese';
  }
  if (
    /\bexpert deck\b/i.test(text) ||
    /\bgx starter\b/i.test(text) ||
    /\bvs (deck|pack)\b/i.test(text) ||
    /\b(corocoro|coro coro)\b/i.test(text) ||
    /\bvmax climax\b/i.test(text) ||
    /\bvstar universe\b/i.test(text) ||
    /\beevee heroes\b/i.test(text) ||
    /\bshiny treasure\b/i.test(text)
  ) {
    return 'japanese';
  }

  // ---- Korean ----
  if (lower.includes('(kr)') || lower.includes('korean') || /\bkor\b/.test(lower)) {
    return 'korean';
  }
  // Hangul ⇒ Korean
  if (/[\uac00-\ud7af]/.test(text)) {
    return 'korean';
  }

  // ---- Chinese ----
  if (lower.includes('(tc)') || lower.includes('t-chinese') || lower.includes('traditional chinese') || /繁(體|体)?中?/.test(text)) {
    return 'chinese_traditional';
  }
  if (
    lower.includes('(cn)') ||
    lower.includes('(sc)') ||
    lower.includes('chinese') ||
    lower.includes('(china)') ||
    lower.includes('s-chinese') ||
    lower.includes('simplified chinese') ||
    /简(體|体)?中?/.test(text)
  ) {
    return 'chinese_simplified';
  }
  // Generic CJK Han fallback (no kana/hangul/explicit traditional markers) ⇒
  // assume simplified Chinese. Anything Japanese has already returned above.
  if (/[\u4e00-\u9fff]/.test(text)) {
    return 'chinese_simplified';
  }

  // Default to English for unlabeled products
  return 'english';
}

/**
 * Parse price string with commas and convert to cents
 * Examples: "1,300" → 130000, "3,302.52" → 330252, "0" → 0
 */
export function parsePriceToCents(priceStr: string): number | null {
  if (!priceStr || priceStr === '0') {
    return null;
  }
  
  // Remove commas and any currency symbols
  const cleaned = priceStr.replace(/[,$€£¥]/g, '').trim();
  
  const value = parseFloat(cleaned);
  
  if (isNaN(value) || value <= 0) {
    return null;
  }
  
  return Math.round(value * 100);
}

/**
 * Parse Collectr date format to ISO date string
 * Expected format: "2026-01-19"
 */
export function parseDate(dateStr: string): string | null {
  if (!dateStr) return null;
  
  // Already in ISO format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  
  // Try to parse other formats
  const date = new Date(dateStr);
  if (!isNaN(date.getTime())) {
    return date.toISOString().split('T')[0];
  }
  
  return null;
}

// Game detection is now handled by src/lib/gameDetection.ts
// The detectGame function is imported and re-exported for backward compatibility

/**
 * Parse a single Collectr CSV row into a structured item
 */
export function parseCollectrRow(row: CollectrRow, lineNumber: number): ParsedCollectrItem | { error: string } {
  try {
    // Detect category
    const category = detectCategory(row.grade, row.cardNumber);
    
    // Parse quantity
    const quantity = parseInt(row.quantity, 10);
    if (isNaN(quantity) || quantity < 1) {
      return { error: 'Invalid quantity' };
    }
    
    // Parse grade for slabs
    const gradeInfo = parseGrade(row.grade);
    
    // Parse variance for finish/edition
    const varianceInfo = parseVariance(row.variance);
    
    // Map condition - for slabs, default to mint (grade is authoritative)
    const condition = category === 'slabs' ? 'mint' : mapCondition(row.cardCondition);
    
    // Detect language from product name OR set name — prefer any non-English signal.
    const productLang = detectLanguage(row.productName);
    const setLang = detectLanguage(row.set);
    const language = productLang !== 'english' ? productLang : setLang;
    
    // Detect game — trust Collectr's Category / Portfolio columns first, then keywords.
    const game = detectGameFromCollectr(row.category, row.portfolioName, row.productName, row.set).game;

    
    // Parse financial data
    const costBasisCents = parsePriceToCents(row.averageCostPaid);
    const marketPriceCents = parsePriceToCents(row.marketPrice);
    const priceOverrideCents = parsePriceToCents(row.priceOverride);
    
    // Parse date
    const purchaseDate = parseDate(row.dateAdded);
    
    // Build the item
    const item: ParsedCollectrItem = {
      title: row.productName.trim(),
      category,
      condition,
      quantity,
      set_name: row.set.trim() || undefined,
      card_number: row.cardNumber.trim() || undefined,
      rarity: normalizeRarity(row.rarity.trim()) || row.rarity.trim() || undefined,
      finish: normalizeFinish(varianceInfo.finish) || varianceInfo.finish || undefined,
      language: language || 'english',
      game: game || undefined,
      slab_grade: gradeInfo.grade || undefined,
      grading_company: gradeInfo.company || undefined,
      is_premium_grade: gradeInfo.isPremium || undefined,
      is_first_edition: varianceInfo.isFirstEdition || undefined,
      cost_basis_cents: costBasisCents || undefined,
      market_price_cents: marketPriceCents || undefined,
      price_override_cents: priceOverrideCents || undefined,
      purchase_date: purchaseDate || undefined,
      notes: row.notes.trim() || undefined,
    };
    
    // Clean up undefined values
    Object.keys(item).forEach(key => {
      if ((item as any)[key] === undefined) {
        delete (item as any)[key];
      }
    });
    
    return item;
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Parse error' };
  }
}

/**
 * Parse entire Collectr CSV into structured items
 */
export function parseCollectrCSV(csvText: string): CollectrImportResult {
  const lines = csvText.split('\n').filter(line => line.trim());
  
  if (lines.length < 2) {
    return { success: [], failures: [{ line: 1, row: '', reason: 'No data rows found' }] };
  }
  
  // Skip header row
  const headerRow = lines[0];
  const dataRows = lines.slice(1);
  
  const success: ParsedCollectrItem[] = [];
  const failures: Array<{ line: number; row: string; reason: string }> = [];
  
  for (let i = 0; i < dataRows.length; i++) {
    const lineNumber = i + 2; // +2 because 1-indexed and skipping header
    const rawLine = dataRows[i];
    
    // Skip empty lines
    if (!rawLine.trim()) continue;
    
    // Parse CSV fields
    const fields = parseCSVLine(rawLine);
    
    // Map to CollectrRow structure
    // Columns: Portfolio Name, Category, Set, Product Name, Card Number, Rarity, 
    //          Variance, Grade, Card Condition, Average Cost Paid, Quantity, 
    //          Market Price, Price Override, Watchlist, Date Added, Notes
    if (fields.length < 16) {
      failures.push({ 
        line: lineNumber, 
        row: rawLine.substring(0, 50) + '...', 
        reason: `Expected 16 columns, got ${fields.length}` 
      });
      continue;
    }
    
    const row: CollectrRow = {
      portfolioName: fields[0],
      category: fields[1],
      set: fields[2],
      productName: fields[3],
      cardNumber: fields[4],
      rarity: fields[5],
      variance: fields[6],
      grade: fields[7],
      cardCondition: fields[8],
      averageCostPaid: fields[9],
      quantity: fields[10],
      marketPrice: fields[11],
      priceOverride: fields[12],
      watchlist: fields[13],
      dateAdded: fields[14],
      notes: fields[15],
    };
    
    const result = parseCollectrRow(row, lineNumber);
    
    if ('error' in result) {
      failures.push({ 
        line: lineNumber, 
        row: row.productName.substring(0, 40) || rawLine.substring(0, 40), 
        reason: result.error 
      });
    } else {
      success.push(result);
    }
  }
  
  return { success, failures };
}

/**
 * Get summary statistics for parsed items
 */
export function getImportSummary(items: ParsedCollectrItem[]): {
  totalItems: number;
  totalQuantity: number;
  sealed: number;
  singles: number;
  slabs: number;
} {
  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  
  return {
    totalItems: items.length,
    totalQuantity,
    sealed: items.filter(i => i.category === 'sealed').length,
    singles: items.filter(i => i.category === 'singles').length,
    slabs: items.filter(i => i.category === 'slabs').length,
  };
}

// ============ SYNC UTILITIES ============

// Cache for set name → code mapping (loaded from sc_pokemon_sets table)
let setNameToCodeCache: Map<string, string> | null = null;

/**
 * Load set name-to-code mapping from the database for better matching.
 * This should be called before sync analysis.
 */
export async function loadSetNameMapping(supabaseClient: any): Promise<void> {
  if (setNameToCodeCache) return;
  
  const { data } = await supabaseClient
    .from('sc_pokemon_sets')
    .select('name, code');
  
  setNameToCodeCache = new Map();
  if (data) {
    for (const set of data) {
      // Store multiple variations for fuzzy matching
      const nameLower = set.name.toLowerCase();
      setNameToCodeCache.set(nameLower, set.code);
      
      // Also store without special characters (em-dash, en-dash, etc.)
      const normalized = nameLower.replace(/[—–\-]/g, ' ').replace(/\s+/g, ' ').trim();
      if (normalized !== nameLower) {
        setNameToCodeCache.set(normalized, set.code);
      }
    }
  }
}

/**
 * Resolve a set name to its code using the cached mapping.
 */
export function resolveSetCode(setName: string): string | null {
  if (!setNameToCodeCache || !setName) return null;
  const normalized = setName.toLowerCase().replace(/[—–\-]/g, ' ').replace(/\s+/g, ' ').trim();
  return setNameToCodeCache.get(normalized) || setNameToCodeCache.get(setName.toLowerCase()) || null;
}

/**
 * Clear the set name mapping cache (useful for testing)
 */
export function clearSetNameCache(): void {
  setNameToCodeCache = null;
}

// ============ NORMALIZE HELPERS FOR MATCHING ============

/**
 * Normalize a title for fuzzy matching:
 * - Lowercase
 * - Remove punctuation
 * - Collapse whitespace  
 * - Remove common suffixes that may vary between sources
 */
function normalizeTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^\w\s]/g, '') // Remove punctuation
    .replace(/\s+/g, ' ')    // Collapse whitespace
    .replace(/\b(full art|alt art|special art|illustration rare|promo|holo|reverse)\b/gi, '') // Remove common suffixes
    .trim();
}

/**
 * Normalize a set identifier for matching:
 * - Lowercase
 * - Remove brackets and language tags
 * - Collapse whitespace
 */
function normalizeSetIdentifier(setValue: string): string {
  return setValue
    .toLowerCase()
    .replace(/[()[\]]/g, '') // Remove brackets
    .replace(/\s+(japanese|jp|english|en|chinese|cn|korean|kr)\s*/gi, '') // Remove language tags
    .replace(/[—–\-]/g, ' ') // Normalize dashes
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Normalize a card number for matching:
 * - Remove leading zeros
 * - Remove "#" prefix
 * - Remove "/total" suffix
 */
function normalizeCardNumber(cardNum: string): string {
  if (!cardNum) return '';
  return cardNum
    .toLowerCase()
    .replace(/^#/, '')           // Remove # prefix
    .replace(/^0+/, '')          // Remove leading zeros
    .replace(/\/\d+$/, '')       // Remove /total suffix
    .trim();
}

/**
 * Minimal item interface for sync matching (existing DB items)
 */
export interface ExistingItem {
  id: string;
  title: string;
  category: ListingCategory;
  condition: string;
  quantity: number;
  set_code?: string | null;
  set_name?: string | null;
  card_number?: string | null;
  grading_company?: string | null;
  slab_grade?: number | null;
  finish?: string | null;
  cost_basis_cents?: number | null;
  language?: string | null;
  // Added for protection during sync
  visibility?: string | null;
  is_listed?: boolean | null;
  // Origin tracking — only items with import_source='collectr' can be flagged as
  // "missing from CSV". Manually-added items are invisible to the diff engine.
  import_source?: string | null;
  // Collectr-derived prices stashed at import time. Used to detect backfill needs.
  collectr_market_price_cents?: number | null;
  collectr_price_override_cents?: number | null;
  // Stable identity stamped on first import. Survives later CardSimp-side edits
  // (rename, set fix, finish change) so re-syncs still match the same row.
  collectr_fingerprint?: string | null;
}


/**
 * Generate a unique matching key for an item based on identifying properties.
 * This key is used to match imported items with existing DB items.
 * Uses normalized values for fuzzy matching.
 */
export function generateItemMatchKey(item: {
  title: string;
  category: ListingCategory;
  condition?: string;
  set_code?: string | null;
  set_name?: string | null;
  card_number?: string | null;
  grading_company?: string | null;
  slab_grade?: number | null;
  finish?: string | null;
}): string {
  // Resolve set code from set name if we have the mapping
  const setCode = item.set_code || resolveSetCode(item.set_name || '') || '';
  const setIdentifier = setCode || normalizeSetIdentifier(item.set_name || '');
  
  return [
    normalizeTitle(item.title),
    item.category,
    setIdentifier,
    normalizeCardNumber(item.card_number || ''),
    (item.grading_company || '').toLowerCase().trim(),
    item.slab_grade?.toString() || '',
    (item.finish || '').toLowerCase().trim(),
  ].join('|');
}

/**
 * Generate a looser match key for secondary matching pass.
 * Uses only the most identifying fields.
 */
export function generateLooseMatchKey(item: {
  title: string;
  category: ListingCategory;
  card_number?: string | null;
}): string {
  // Take first 3 words of normalized title for looser matching
  const titleWords = normalizeTitle(item.title).split(' ').slice(0, 3).join(' ');
  return [
    titleWords,
    item.category,
    normalizeCardNumber(item.card_number || ''),
  ].join('|');
}

/**
 * Compute the stable fingerprint we stamp on a Collectr-imported item.
 *
 * It's just the strict match key derived from the CSV row's values. Because
 * we freeze it at first import and never recompute from live CardSimp fields,
 * later user edits (rename, set fix, finish change) cannot break re-matching:
 * the same CSV row always produces the same fingerprint.
 */
export function computeCollectrFingerprint(item: ParsedCollectrItem): string {
  return generateItemMatchKey({
    title: item.title,
    category: item.category,
    condition: item.condition,
    set_name: item.set_name,
    card_number: item.card_number,
    grading_company: item.grading_company,
    slab_grade: item.slab_grade,
    finish: item.finish,
  });
}


/**
 * Result of sync analysis comparing imported items vs existing DB items
 */
export interface SyncAnalysisResult {
  // Items in import that don't exist in DB
  newItems: ParsedCollectrItem[];
  // Items in both - import data may have updates (qty, cost)
  updatedItems: Array<{
    existing: ExistingItem;
    imported: ParsedCollectrItem;
    changes: string[]; // e.g. ["quantity: 2 → 3", "cost: $10 → $15"]
    partialSold?: number; // If quantity decreased, how many were potentially sold
    /** Listed items: only refresh stashed Collectr prices, don't touch qty/cost/asking_price. */
    priceOnly?: boolean;
  }>;
  // Items in DB but not in import (possibly sold)
  removedItems: ExistingItem[];
  // Items in DB that matched and have no changes
  unchangedItems: ExistingItem[];
  // Listed items that are protected from changes during sync
  protectedItems: Array<{
    existing: ExistingItem;
    imported: ParsedCollectrItem;
    reason: 'listed';
  }>;
}

/**
 * Analyze differences between imported Collectr data and existing DB items.
 * Supports loose matching fallback and protects listed items.
 */
export function analyzeSync(
  existingItems: ExistingItem[],
  importedItems: ParsedCollectrItem[]
): SyncAnalysisResult {
  // (maps declared below)


  // Build maps by strict match key
  const existingByKey = new Map<string, ExistingItem>();
  // Loose-key index is a multimap so colliding items (same first-3-words +
  // category + card #, different finish/grade) don't get silently dropped and
  // mis-reported as "removed" on every re-sync.
  const existingByLooseKey = new Map<string, ExistingItem[]>();
  // Frozen-at-first-import identity. Beats both strict + loose keys because it
  // doesn't depend on the user's current CardSimp values.
  const existingByFingerprint = new Map<string, ExistingItem>();

  for (const item of existingItems) {
    const key = generateItemMatchKey({
      title: item.title,
      category: item.category,
      condition: item.condition,
      set_code: item.set_code,
      set_name: item.set_name,
      card_number: item.card_number,
      grading_company: item.grading_company,
      slab_grade: item.slab_grade,
      finish: item.finish,
    });
    existingByKey.set(key, item);

    const looseKey = generateLooseMatchKey({
      title: item.title,
      category: item.category,
      card_number: item.card_number,
    });
    const bucket = existingByLooseKey.get(looseKey);
    if (bucket) bucket.push(item);
    else existingByLooseKey.set(looseKey, [item]);

    if (item.collectr_fingerprint) {
      // First write wins; duplicates would only happen from manual SQL.
      if (!existingByFingerprint.has(item.collectr_fingerprint)) {
        existingByFingerprint.set(item.collectr_fingerprint, item);
      }
    }
  }

  const importedByKey = new Map<string, ParsedCollectrItem>();

  for (const item of importedItems) {
    const key = generateItemMatchKey({
      title: item.title,
      category: item.category,
      condition: item.condition,
      set_name: item.set_name,
      card_number: item.card_number,
      grading_company: item.grading_company,
      slab_grade: item.slab_grade,
      finish: item.finish,
    });
    importedByKey.set(key, item);
  }


  const newItems: ParsedCollectrItem[] = [];
  const updatedItems: SyncAnalysisResult['updatedItems'] = [];
  const unchangedItems: ExistingItem[] = [];
  const removedItems: ExistingItem[] = [];
  const protectedItems: SyncAnalysisResult['protectedItems'] = [];

  const matchedExistingIds = new Set<string>();

  // Score a loose-match candidate vs an imported row. Higher = better fit.
  const scoreLoose = (cand: ExistingItem, imp: ParsedCollectrItem): number => {
    let s = 0;
    const candSet = (cand.set_code || cand.set_name || '').toLowerCase().trim();
    const impSet = (imp.set_name || '').toLowerCase().trim();
    if (candSet && impSet && (candSet === impSet || candSet.includes(impSet) || impSet.includes(candSet))) s += 3;
    if ((cand.grading_company || '').toLowerCase() === (imp.grading_company || '').toLowerCase()) s += 3;
    if ((cand.slab_grade ?? null) === (imp.slab_grade ?? null)) s += 1;
    if ((cand.finish || '').toLowerCase() === (imp.finish || '').toLowerCase()) s += 2;
    if ((cand.cost_basis_cents ?? null) === (imp.cost_basis_cents ?? null)) s += 1;
    return s;
  };

  // Find new and updated items
  for (const [key, imported] of importedByKey) {
    // 1) Fingerprint match first — survives user edits to title/set/finish.
    const fp = computeCollectrFingerprint(imported);
    let existing = existingByFingerprint.get(fp);
    if (existing && matchedExistingIds.has(existing.id)) existing = undefined;
    // 2) Strict key fallback (today's path).
    if (!existing) existing = existingByKey.get(key);


    // Fallback: scored loose match against only-still-unmatched candidates.
    if (!existing) {
      const looseKey = generateLooseMatchKey({
        title: imported.title,
        category: imported.category,
        card_number: imported.card_number,
      });
      const candidates = (existingByLooseKey.get(looseKey) || []).filter(
        (c) => !matchedExistingIds.has(c.id)
      );
      if (candidates.length) {
        let best: ExistingItem | undefined;
        let bestScore = -1;
        for (const c of candidates) {
          const s = scoreLoose(c, imported);
          if (s > bestScore) { bestScore = s; best = c; }
        }
        // Threshold prevents pairing wildly different siblings (e.g. raw vs PSA 10).
        if (best && bestScore >= 2) existing = best;
      }
    }
    
    if (!existing) {
      newItems.push(imported);
    } else {
      matchedExistingIds.add(existing.id);
      
      // Listed items: still protected from qty/cost/asking_price changes, but
      // we DO allow stashed Collectr prices (collectr_market_price_cents /
      // collectr_price_override_cents) to refresh so the seller can re-apply
      // them manually via the gold "Use $X.XX" pill.
      const isListed = existing.visibility === 'listed' || existing.is_listed;
      if (isListed) {
        const existingMarket = existing.collectr_market_price_cents ?? null;
        const importedMarket = imported.market_price_cents ?? null;
        const existingOverride = existing.collectr_price_override_cents ?? null;
        const importedOverride = imported.price_override_cents ?? null;
        const priceDiff = existingMarket !== importedMarket || existingOverride !== importedOverride;
        if (priceDiff) {
          updatedItems.push({
            existing,
            imported,
            changes: ['Collectr price (listed — won\'t change live price)'],
            priceOnly: true,
          });
        } else {
          protectedItems.push({ existing, imported, reason: 'listed' });
        }
        continue;
      }
      
      
      // Check for changes
      const changes: string[] = [];
      let partialSold: number | undefined;
      
      if (existing.quantity !== imported.quantity) {
        changes.push(`qty: ${existing.quantity} → ${imported.quantity}`);
        
        // Track potential partial sales
        if (existing.quantity > imported.quantity) {
          partialSold = existing.quantity - imported.quantity;
        }
      }
      
      const existingCost = existing.cost_basis_cents;
      const importedCost = imported.cost_basis_cents;
      if (existingCost !== importedCost) {
        const formatCost = (cents: number | null | undefined) => 
          cents ? `${formatUsdCents(cents)}` : 'none';
        changes.push(`cost: ${formatCost(existingCost)} → ${formatCost(importedCost)}`);
      }

      // Backfill / refresh Collectr-derived prices when they differ.
      // This is how a re-sync of an old CSV populates new collectr price columns
      // on items imported before the feature existed.
      const existingMarket = existing.collectr_market_price_cents ?? null;
      const importedMarket = imported.market_price_cents ?? null;
      const existingOverride = existing.collectr_price_override_cents ?? null;
      const importedOverride = imported.price_override_cents ?? null;
      if (existingMarket !== importedMarket || existingOverride !== importedOverride) {
        changes.push('collectr price');
      }

      if (changes.length > 0) {
        updatedItems.push({ existing, imported, changes, partialSold });
      } else {
        unchangedItems.push(existing);
      }
    }
  }
  
  // Find removed items (in DB but not in import).
  // CRITICAL: only consider items that originally came from a Collectr import.
  // Manually-added items must never be flagged as "missing" just because they're
  // not in the CSV — they were never expected to be there.
  for (const existing of existingItems) {
    if (matchedExistingIds.has(existing.id)) continue;
    if (existing.import_source !== 'collectr') continue;
    removedItems.push(existing);
  }
  
  return { newItems, updatedItems, removedItems, unchangedItems, protectedItems };
}

/**
 * Get sync summary for display
 */
export function getSyncSummary(analysis: SyncAnalysisResult): {
  newCount: number;
  updatedCount: number;
  removedCount: number;
  unchangedCount: number;
  protectedCount: number;
  partialSoldCount: number;
} {
  return {
    newCount: analysis.newItems.length,
    updatedCount: analysis.updatedItems.length,
    removedCount: analysis.removedItems.length,
    unchangedCount: analysis.unchangedItems.length,
    protectedCount: analysis.protectedItems.length,
    partialSoldCount: analysis.updatedItems.filter(u => u.partialSold && u.partialSold > 0).length,
  };
}
